// frontend/src/modules/reports/pages/workforce/TimelinePage.tsx
//
// Phase 4 (Reports Frontend), Task 3. Timeline — a single employee's single
// day: attendance sessions (with nested pauses, indented + kind-labeled),
// the day's time entries (project/task names), and a screenshot count. This
// report is single-date, not range-based, so it uses ReportShell's
// `noDateRange` mode and builds its own employee + date picker in the
// filter slot. The "View in monitoring" link only renders when the current
// user holds `monitoring.view_all` — screenshot URLs/viewer stay behind
// monitoring's own permission, this page only ever surfaces a COUNT.

import * as React from 'react';
import { Link } from 'react-router-dom';
import { Camera, Coffee, PauseCircle } from 'lucide-react';
import { ReportShell } from '../../components/ReportShell';
import { StatCard } from '../../../../design-system/components/StatCard';
import { useApi } from '../../../../design-system/useApi';
import { reportUrl, downloadReportCsv } from '../../api';
import type { TimelineReport } from '../../types';
import { VISUAL } from '../../../../design-system/visual';
import { formatDateTime } from '../../../../design-system/format';
import { usePermission } from '../../../../core/auth/usePermission';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../../components/ui/select';
import { Input } from '../../../../components/ui/input';
import type { EmployeeWithUser } from '../../../hr/types';

function isoDay(d: Date): string { return d.toISOString().slice(0, 10); }

const PAUSE_LABELS: Record<'break' | 'idle', string> = { break: 'Break', idle: 'Idle' };

export function TimelinePage() {
  const [employeeId, setEmployeeId] = React.useState<string>('');
  const [date, setDate] = React.useState<string>(() => isoDay(new Date()));
  const canViewMonitoring = usePermission('monitoring.view_all');

  const empResult = useApi<{ employees: EmployeeWithUser[] }>('/api/hr/employees');
  const employees = empResult.data?.employees ?? [];
  // The report is gated on attendance.view_all, but this picker reads the
  // employee directory, which needs employees.view. A role holding only the
  // first got no picker AND the standing prompt "Select an employee" — an
  // instruction it was impossible to follow, with nothing saying why.
  const employeesUnavailable = !empResult.loading && employees.length === 0 && empResult.error != null;

  const path = employeeId ? reportUrl('timeline', { employeeId, date }) : null;
  const result = useApi<{ report: TimelineReport }>(path, [employeeId, date]);
  const forbidden = result.status === 403;

  const report = result.data?.report ?? null;
  const sessions = report?.sessions ?? [];
  const entries = report?.time_entries ?? [];

  return (
    <ReportShell
      eyebrow="Reports / Workforce"
      title="Timeline"
      description="A single employee's day: sessions, pauses, time entries, and screenshot activity."
      noDateRange
      access={!forbidden}
      accessDescription={forbidden ? (result.error ?? undefined) : undefined}
      loading={!forbidden && employeeId !== '' && result.loading}
      error={forbidden ? null : result.error}
      onRetry={result.reload}
      isEmpty={
        !forbidden
        && employeeId !== ''
        && !result.loading
        && !result.error
        && sessions.length === 0
        && entries.length === 0
      }
      emptyTitle="No data for this day"
      emptyDescription="This employee has no sessions or time entries on the selected date."
      filters={
        <>
          {employees.length > 0 && (
            <Select value={employeeId} onValueChange={setEmployeeId}>
              <SelectTrigger className="h-9 rounded-full bg-card/80 border-border/60 px-3.5 text-[12.5px] font-semibold w-auto min-w-[180px]">
                <SelectValue placeholder="Select an employee" />
              </SelectTrigger>
              <SelectContent>
                {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="h-9 w-40"
          />
        </>
      }
      csv={employeeId ? {
        filename: `timeline-${employeeId}-${date}.csv`,
        onDownload: () => downloadReportCsv('timeline', { employeeId, date }, `timeline-${employeeId}-${date}.csv`),
      } : null}
    >
      {employeesUnavailable ? (
        <div className={[VISUAL.bento, 'overflow-hidden p-8 text-center'].join(' ')}>
          <p className="text-sm font-semibold text-foreground">The employee list didn't load.</p>
          <p className="mt-1.5 text-sm text-muted-foreground">{empResult.error}</p>
          <p className="mt-1.5 text-[13px] text-muted-foreground">
            This report picks an employee from the directory, which needs the “View employees”
            permission. Ask an owner to grant it, or use another workforce report.
          </p>
        </div>
      ) : employeeId === '' ? (
        <div className={[VISUAL.bento, 'overflow-hidden p-8 text-center text-sm text-muted-foreground'].join(' ')}>
          Select an employee to see their timeline.
        </div>
      ) : report && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard label="Sessions" value={sessions.length} />
            <StatCard label="Time entries" value={entries.length} />
            <StatCard label="Screenshots" value={report.screenshot_count} icon={<Camera />} />
          </div>

          {canViewMonitoring && report.screenshot_count > 0 && (
            <Link
              to="/dashboard/monitoring"
              className="inline-flex items-center text-[13px] font-semibold text-saffron hover:underline"
            >
              View in monitoring →
            </Link>
          )}

          <div className="space-y-2">
            <h3 className="text-[13px] font-semibold text-foreground px-1">Sessions</h3>
            <div className={[VISUAL.bento, 'overflow-hidden divide-y divide-border/50'].join(' ')}>
              {sessions.length === 0 ? (
                <div className="p-6 text-sm text-muted-foreground">No sessions this day.</div>
              ) : sessions.map((s) => (
                <div key={s.id} className="p-4">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-foreground text-[13.5px]">
                      {formatDateTime(s.clock_in_at)} – {s.clock_out_at ? formatDateTime(s.clock_out_at) : 'still open'}
                    </span>
                    <span className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground/70">{s.source}</span>
                  </div>
                  {s.notes && <p className="text-[12.5px] text-muted-foreground mt-1">{s.notes}</p>}
                  {s.pauses.length > 0 && (
                    <ul className="mt-2 ml-4 space-y-1 border-l-2 border-border/60 pl-3">
                      {s.pauses.map((p) => (
                        <li key={p.id} className="flex items-center gap-2 text-[12.5px] text-muted-foreground">
                          {p.kind === 'break' ? <Coffee className="h-3.5 w-3.5" /> : <PauseCircle className="h-3.5 w-3.5" />}
                          <span className="font-medium text-foreground/80">{PAUSE_LABELS[p.kind]}</span>
                          <span>
                            {formatDateTime(p.started_at)} – {p.ended_at ? formatDateTime(p.ended_at) : 'ongoing'}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <h3 className="text-[13px] font-semibold text-foreground px-1">Time entries</h3>
            <div className={[VISUAL.bento, 'overflow-hidden divide-y divide-border/50'].join(' ')}>
              {entries.length === 0 ? (
                <div className="p-6 text-sm text-muted-foreground">No time entries this day.</div>
              ) : entries.map((e) => (
                <div key={e.id} className="p-4 flex items-center justify-between gap-2">
                  <div>
                    <div className="font-medium text-foreground text-[13.5px]">
                      {e.project_name}{e.task_name ? ` · ${e.task_name}` : ''}
                    </div>
                    {e.notes && <p className="text-[12.5px] text-muted-foreground mt-0.5">{e.notes}</p>}
                  </div>
                  <span className="text-[12.5px] text-muted-foreground whitespace-nowrap">
                    {formatDateTime(e.start_at)} – {e.end_at ? formatDateTime(e.end_at) : 'ongoing'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </ReportShell>
  );
}
